<template>
  <v-card class="flex d-flex flex-column">
    <v-form class="col-xl-8 pa-0" novalidate @submit.prevent>
      <v-card-title class="mb-4">
        <span class="headline">{{ $t('adminCompanyPreferences.title') }}</span>
      </v-card-title>
      <v-card-text class="text--primary">
        <v-row>
          <v-col class="title">Contract Parameters</v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            Preferred Collateral Rate
            <small class="d-block text--secondary label-note">
              Your Preferred Collateral Rate is applied to all new contracts for which you are the
              lender.
            </small>
          </v-col>
          <v-col cols="4">
            <numeric-input
              v-model="preferredCollateralRate"
              :error-messages="errorMsgs['preferredCollateralRate']"
              :max="maxCR"
              :min="minCR"
              :step="1"
              suffix="%"
              type="integer"
              @blur="$v.preferredCollateralRate.$touch()"
              @input="$v.preferredCollateralRate.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            Preferred Rounding Rule
            <small class="d-block text--secondary label-note">
              Your Preferred Rounding Rule is applied to all new contracts for which you are the
              lender.
            </small>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="preferredRoundingRule"
              :items="roundingRuleOptions"
              @blur="$v.preferredRoundingRule.$touch()"
              @input="$v.preferredRoundingRule.$touch()"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text class="text--primary">
        <v-row>
          <v-col class="title">Default Risk Limits</v-col>
        </v-row>
        <v-row>
          <v-col cols="7">
            <v-alert class="text-sm-body-2" color="secondary" dense type="info">
              Default Risk Limits are applied when individual risk limits are not configured for a
              trader.
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="subtitle-1"> Per Order Soft Limit</v-col>
        </v-row>
        <v-row>
          <v-col>
            Fat-finger warning will trigger when a trader attempts to enter an order exceeding this
            notional value.
          </v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Default Per Order Soft Limit:</v-col>
          <v-col cols="4">
            <numeric-input
              ref="softLimitInput"
              v-model="defaultOrderSoftLimit"
              :clearable="true"
              :error-messages="errorMsgs.defaultOrderSoftLimit"
              :min="1"
              :precision="0"
              prefix="$"
              type="decimal"
              @blur="$v.defaultOrderSoftLimit.$touch()"
              @input="$v.defaultOrderSoftLimit.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-6 pb-0">
            <div class="subtitle-1">Per Order Hard Limit</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            Hard limit will be enforced when a trader attempts to enter an order exceeding this
            notional value, preventing order entry.
          </v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Default Per Order Hard Limit:</v-col>
          <v-col cols="4">
            <numeric-input
              ref="hardLimitInput"
              v-model="defaultOrderHardLimit"
              :clearable="true"
              :error-messages="errorMsgs.defaultOrderHardLimit"
              :min="1"
              :precision="0"
              prefix="$"
              type="decimal"
              @blur="$v.defaultOrderHardLimit.$touch()"
              @input="$v.defaultOrderHardLimit.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-6 pb-0">
            <div class="subtitle-1">Aggregate Exposure Limit</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            Total notional value of orders allowed to be exposed in the Marketplace at any given
            time.
          </v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Default Aggregate Exposure Limit:</v-col>
          <v-col cols="4">
            <numeric-input
              ref="aggregateExposureLimitInput"
              v-model="aggregateExposureLimit"
              :clearable="true"
              :error-messages="errorMsgs.aggregateExposureLimit"
              :min="1"
              :precision="0"
              prefix="$"
              type="decimal"
              @blur="$v.aggregateExposureLimit.$touch()"
              @input="$v.aggregateExposureLimit.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-6 pb-0">
            <div class="subtitle-1">Executions Value Limit</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            The aggregate total notional value of executions as a daily limit; acts as a cutoff
            preventing further order creation over the cumulative value of executions
            (non-cancelled) + open, firm order market value.
          </v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Executions Value Limit:</v-col>
          <v-col cols="4">
            <numeric-input
              ref="executionsValueLimitInput"
              v-model="executionsValueLimit"
              :clearable="true"
              :error-messages="errorMsgs.executionsValueLimit"
              :min="1"
              :precision="0"
              prefix="$"
              type="decimal"
              @blur="$v.executionsValueLimit.$touch()"
              @input="$v.executionsValueLimit.$touch()"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text class="text--primary">
        <v-row>
          <v-col class="title">Benchmark</v-col>
        </v-row>
        <v-row>
          <v-col> Benchmark rate to be used for calculations in company reports. </v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Default Benchmark:</v-col>
          <v-col cols="4">
            <v-select
              v-model="benchmarkType"
              :items="benchmarkOptions"
              @blur="$v.benchmarkType.$touch()"
            />
            <numeric-input
              v-if="benchmarkType == 'Custom'"
              v-model="benchmarkPrice"
              :autofocus="true"
              :error-messages="errorMsgs.benchmarkPrice"
              :min="0"
              :precision="2"
              type="decimal"
              @focus="$v.benchmarkPrice.$reset()"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="text--primary">
        <v-row>
          <v-col class="title">Marketplace Defaults</v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Default Agreements:</v-col>
          <v-col cols="4">
            <multiple-chip-agreement-selector
              :agreements.sync="defaultOmsAgreements"
              label="Agreements"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="loginState.user?.companySmartloanEnabled" class="text--primary">
        <v-row>
          <v-col class="title">Smartloan Agreements</v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">Smartloan Agreements:</v-col>
          <v-col cols="4">
            <multiple-chip-agreement-selector
              :agreements.sync="smartloanAgreements"
              label="Agreements"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="canConfigCutOffTime" class="text--primary">
        <v-row>
          <v-col class="title">Cutoff time</v-col>
        </v-row>
        <v-row align="center" dense>
          <v-col cols="4">
            <p class="mt-4">
              Return Cutoff Time (EST). Any returns initiated after this time will require the
              lender to approve the return before they get officially recorded to the trade and/or
              settlement instructions sent.
            </p>
          </v-col>
          <v-col cols="4">
            <v-menu
              ref="timePickerMenu"
              v-model="isTimePickerOpen"
              :close-on-content-click="false"
              max-width="290px"
              min-width="290px"
              :nudge-right="40"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  clearable
                  placeholder="Select a cutoff time"
                  readonly
                  :value="amPmTime"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="cutOffTime = null"
                />
              </template>
              <v-time-picker v-if="isTimePickerOpen" v-model="cutOffTime" full-width />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="text--primary">
        <v-row>
          <v-col class="title">User Security</v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="4">
            Auto-disable Inactive Users
            <small class="d-block text--secondary label-note">
              Automatically disable users who haven't logged in for a specified number of days.
            </small>
          </v-col>
          <v-col cols="1">
            <v-switch
              v-model="autoDisableInactiveEnabled"
              data-test="auto-disable-users-switch"
              hide-details
              label="Enable"
            />
          </v-col>
          <v-col cols="2">
            <numeric-input
              v-if="autoDisableInactiveEnabled"
              v-model="autoDisableInactiveDays"
              class="mt-1"
              data-test="auto-disable-users-days"
              :error-messages="errorMsgs.autoDisableInactiveDays"
              :min="autoDisableInactiveDaysMin"
              :precision="0"
              suffix="days"
              type="integer"
              @blur="$v.autoDisableInactiveDays.$touch()"
              @input="$v.autoDisableInactiveDays.$touch()"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <div class="pre-footer-spacer"></div>

      <v-card-actions class="pa-3 fixed-footer">
        <v-row>
          <v-col>
            <v-btn
              class="px-6"
              color="primary"
              :disabled="isProcessing"
              type="submit"
              @click="submitForm()"
            >
              {{ $t('saveButton') }}
            </v-btn>
          </v-col>
          <v-col>
            <div v-if="showError || showSuccess">
              <div
                class="v-alert v-alert--dense text--primary text-body-2 text-center ma-0"
                :class="{ error: showError, success: showSuccess }"
              >
                <div v-if="showError">
                  {{ errorMsgs.apiErrors.join('\n') }}
                </div>
                <div v-if="showSuccess">Your company details have been updated.</div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import Component, { mixins } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import axios from 'axios';
import { Route } from 'vue-router';
import Decimal from 'decimal.js';
import { isEqual, isNull } from 'lodash';
import {
  DEFAULT_COLLATERAL_RATE,
  IA_PRECISION,
  MAX_COLLATERAL_RATE,
  MIN_COLLATERAL_RATE,
} from '@/modules/common/constants/precision';
import { ApiError } from '@/utils/errors';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import wait from '@/modules/common/services/wait';
import { validationMixin } from 'vuelidate';
import { decimal, maxValue, minValue, numeric, requiredIf } from 'vuelidate/lib/validators';
import { RoundingRule, roundingRuleToString } from '@/modules/sec-lending/helpers/contract-details';
import { ClientConfig } from '@/utils/helpers/rest';
import { minValueIf } from '@/utils/helpers/risk-limit-validator';
import { getAllBenchmarks } from '@/utils/api/loans';
import { LoginState } from '@/store/store';
import { formatAmPmTime } from '@/utils/helpers/time';
import { TradingPermission, hasOneOfTradingPermission } from '@/utils/helpers/trading-permissions';
import MultipleChipAgreementSelector from '@/modules/agreements/components/MultipleChipAgreementSelector.vue';
import { AgreementInfo } from '@/modules/agreements/models';
import { SelectValues } from '@/types/select';
import { Company, CompanyResponse, RawCompanyResponse } from '@/modules/common/models/admin';

interface FormFields {
  preferredRoundingRule: RoundingRule;
  preferredCollateralRate: number | null;
  defaultOrderSoftLimit: Decimal | null;
  defaultOrderHardLimit: Decimal | null;
  aggregateExposureLimit: Decimal | null;
  executionsValueLimit: Decimal | null;
  benchmarkPrice: Decimal | null;
  defaultOmsAgreements: AgreementInfo[];
  benchmarkType: string | null;
  cutOffTime: string | null;
  autoDisableInactiveEnabled: boolean;
  autoDisableInactiveDays: number | null;
}
interface FormErrors {
  apiErrors: string[];
  preferredCollateralRate: string[];
  defaultOrderSoftLimit: string[];
  defaultOrderHardLimit: string[];
  aggregateExposureLimit: string[];
  benchmarkPrice: string[];
  executionsValueLimit: string[];
  autoDisableInactiveDays: string[];
}

// Register router hooks to use in the component
Component.registerHooks(['beforeRouteLeave']);

@Component({
  components: { MultipleChipAgreementSelector },
  validations: function (this: TraderAdminCompanyPreferences) {
    return {
      benchmarkType: {},
      preferredRoundingRule: {},
      preferredCollateralRate: {
        numeric,
        minValue: minValue(MIN_COLLATERAL_RATE),
        maxValue: maxValue(MAX_COLLATERAL_RATE),
      },
      defaultOrderSoftLimit: {
        numeric,
        minValue: minValue(1),
      },
      defaultOrderHardLimit: {
        numeric,
        minValue: minValue(1),
        minOrderSoftLimitValue: minValueIf(
          !isNull(this.defaultOrderSoftLimit),
          this.defaultOrderSoftLimit
        ),
      },
      aggregateExposureLimit: {
        numeric,
        minValue: minValue(1),
      },
      executionsValueLimit: {
        numeric,
        minValue: minValue(1),
        minAggregateExposureLimit: minValueIf(
          !isNull(this.aggregateExposureLimit),
          this.aggregateExposureLimit
        ),
      },
      benchmarkPrice: {
        requiredIfTypeCustom: requiredIf(() => {
          return this.benchmarkType == 'Custom';
        }),
        decimal,
      },
      autoDisableInactiveDays: {
        required: requiredIf(() => this.autoDisableInactiveEnabled),
        numeric,
        minValue: minValue(this.clientConfig?.autoDisableInactiveDaysMin ?? 90),
      },
    };
  },
  methods: {
    ...mapActions([]),
  },
  computed: {
    ...mapState(['clientConfig', 'loginState']),
  },
})
export default class TraderAdminCompanyPreferences extends mixins(validationMixin) {
  public $refs!: {
    softLimitInput: Vue & { setFocus(): void };
    hardLimitInput: Vue & { setFocus(): void };
    timePickerMenu: Vue;
  };

  public formInitialValues: Partial<FormFields> = {};

  // store state refs
  public clientConfig!: ClientConfig;
  public loginState!: LoginState;

  public preferredCollateralRate: number | null = null;
  public preferredRoundingRule: RoundingRule = RoundingRule.NoRounding;
  public hasPreferredRoundingRule = false;
  public defaultOmsAgreements: AgreementInfo[] = [];
  public smartloanAgreements: AgreementInfo[] = [];
  public benchmarkType: string | null = null;
  public benchmarkPrice: Decimal | null = null;
  public benchmarkOptions = ['Custom', ...getAllBenchmarks().filter((i) => i !== 'NoBenchmark')];
  public cutOffTime: string | null = null;
  public smartLoanIntegrationUrl: string | null = null;
  public smartLoanIntegrationUsername: string | null = null;

  public roundingRuleOptions: SelectValues<RoundingRule> = [
    { value: RoundingRule.NoRounding, text: roundingRuleToString(RoundingRule.NoRounding) },
    {
      value: RoundingRule.UpToNearest0Dot01,
      text: roundingRuleToString(RoundingRule.UpToNearest0Dot01),
    },
    {
      value: RoundingRule.UpToNearest0Dot05,
      text: roundingRuleToString(RoundingRule.UpToNearest0Dot05),
    },
    {
      value: RoundingRule.UpToNearest0Dot10,
      text: roundingRuleToString(RoundingRule.UpToNearest0Dot10),
    },
    {
      value: RoundingRule.UpToNearest0Dot25,
      text: roundingRuleToString(RoundingRule.UpToNearest0Dot25),
    },
    {
      value: RoundingRule.UpToNearest1Dot0,
      text: roundingRuleToString(RoundingRule.UpToNearest1Dot0),
    },
  ];

  public isProcessing = false;
  public showSuccess = false;
  public isTimePickerOpen = false;
  public apiErrors: string[] = [];
  public defaultOrderSoftLimit: Decimal | null = null;
  public defaultOrderHardLimit: Decimal | null = null;
  public aggregateExposureLimit: Decimal | null = null;
  public executionsValueLimit: Decimal | null = null;
  public autoDisableInactiveEnabled = false;
  public autoDisableInactiveDays: number | null = null;
  private minCR = MIN_COLLATERAL_RATE;
  private maxCR = MAX_COLLATERAL_RATE;

  public get errorMsgs(): FormErrors {
    const errors: FormErrors = {
      apiErrors: this.apiErrors,
      preferredCollateralRate: [],
      defaultOrderSoftLimit: [],
      defaultOrderHardLimit: [],
      aggregateExposureLimit: [],
      benchmarkPrice: [],
      executionsValueLimit: [],
      autoDisableInactiveDays: [],
    };

    // rate errors
    if (this.$v.preferredCollateralRate.$dirty) {
      if (!this.$v.preferredCollateralRate.minValue)
        errors.preferredCollateralRate.push('should be at least 100%.');
      if (!this.$v.preferredCollateralRate.maxValue)
        errors.preferredCollateralRate.push(`can't be larger than 199%.`);
    }

    // soft limit errors
    if (!isNull(this.defaultOrderSoftLimit) && this.$v.defaultOrderSoftLimit.$dirty) {
      if (!this.$v.defaultOrderSoftLimit.minValue) {
        errors.defaultOrderSoftLimit.push('please enter a soft limit');
      }
    }

    // hard limit errors
    if (!isNull(this.defaultOrderHardLimit) && this.$v.defaultOrderHardLimit.$dirty) {
      if (!this.$v.defaultOrderHardLimit.minValue) {
        errors.defaultOrderHardLimit.push('please enter a hard limit');
      }

      if (!this.$v.defaultOrderHardLimit.minOrderSoftLimitValue) {
        errors.defaultOrderHardLimit.push('hard limit should always exceed the soft limit');
      }
    }

    if (!isNull(this.aggregateExposureLimit) && this.$v.aggregateExposureLimit.$dirty) {
      if (!this.$v.aggregateExposureLimit.minValue) {
        errors.aggregateExposureLimit.push('please enter a valid aggregate exposure limit');
      }
    }

    if (!isNull(this.executionsValueLimit) && this.$v.executionsValueLimit.$dirty) {
      if (!this.$v.executionsValueLimit.minValue) {
        errors.executionsValueLimit.push('please enter a valid executions value limit');
      }
    }

    if (
      !isNull(this.aggregateExposureLimit) &&
      !isNull(this.executionsValueLimit) &&
      (this.$v.executionsValueLimit.$dirty || this.$v.aggregateExposureLimit.$dirty)
    ) {
      if (!this.$v.executionsValueLimit.minAggregateExposureLimit) {
        errors.executionsValueLimit.push(
          'executions value limit should always exceed the aggregate exposure limit'
        );
      }
    }

    // benchmark price errors
    if (this.$v.benchmarkPrice.$dirty) {
      if (!this.$v.benchmarkPrice.requiredIfTypeCustom) {
        errors.benchmarkPrice.push('please enter a value');
      }
      if (!this.$v.benchmarkPrice.numeric) {
        errors.benchmarkPrice.push('please enter a valid number');
      }
    }

    // auto-disable inactive days errors
    if (this.autoDisableInactiveEnabled && this.$v.autoDisableInactiveDays.$dirty) {
      if (!this.$v.autoDisableInactiveDays.required) {
        errors.autoDisableInactiveDays.push('please enter a value');
      } else if (!this.$v.autoDisableInactiveDays.minValue) {
        errors.autoDisableInactiveDays.push(
          `must be at least ${this.autoDisableInactiveDaysMin} days`
        );
      }
    }

    return errors;
  }

  public get showError(): boolean {
    return !!this.errorMsgs.apiErrors.length;
  }

  public get amPmTime(): string {
    return this.cutOffTime ? formatAmPmTime(this.cutOffTime) : '';
  }

  public get autoDisableInactiveDaysMin(): number {
    return this.clientConfig?.autoDisableInactiveDaysMin ?? 90;
  }

  public get canConfigCutOffTime(): boolean {
    return hasOneOfTradingPermission(
      this.loginState.user?.companyTradingPermissions as TradingPermission,
      TradingPermission.LenderPermissions
    );
  }

  public getFormModelValues(): FormFields {
    return {
      preferredCollateralRate: this.preferredCollateralRate || DEFAULT_COLLATERAL_RATE,
      preferredRoundingRule: this.preferredRoundingRule,
      defaultOrderSoftLimit: this.defaultOrderSoftLimit,
      defaultOrderHardLimit: this.defaultOrderHardLimit,
      aggregateExposureLimit: this.aggregateExposureLimit,
      executionsValueLimit: this.executionsValueLimit,
      defaultOmsAgreements: this.defaultOmsAgreements,
      benchmarkType: this.benchmarkType,
      benchmarkPrice: this.benchmarkPrice,
      cutOffTime: this.cutOffTime,
      autoDisableInactiveEnabled: this.autoDisableInactiveEnabled,
      autoDisableInactiveDays: this.autoDisableInactiveDays,
    };
  }

  public async mounted(): Promise<void> {
    try {
      await this.fetchTraderAdminCompanyPreferences();
      this.$v.$reset();
      this.formInitialValues = this.getFormModelValues();
    } catch (e) {
      this.$log.warn(e);
    }
  }

  public beforeRouteLeave(
    to: Route,
    from: Route,
    next: (to?: string | false | void | undefined) => void
  ): void {
    // check if anything was changed to see if we should let the user confirm he's sure about leaving
    if (this.$v.$anyDirty && !isEqual(this.getFormModelValues(), this.formInitialValues)) {
      this.$dialog.confirm({
        title: 'Are you sure?',
        message:
          'You have unsaved changes in the company preferences form. Are you sure you want to leave without saving?',
        isRejectable: true,
        rejectText: 'Cancel',
        acceptText: 'Continue',
        onAccept: () => {
          next();
        },
        onReject: () => {
          next(false);
        },
      });
    } else {
      next();
    }
  }

  public async getTraderAdminCompanyPreferences(): Promise<Company> {
    const { data } = await axios.get<RawCompanyResponse>(`/api/1/trader-admin/company`);
    return CompanyResponse.fromData(data).company;
  }

  public async fetchTraderAdminCompanyPreferences(): Promise<void> {
    const companyPreferences = await this.getTraderAdminCompanyPreferences();

    this.preferredCollateralRate =
      companyPreferences.preferredIndependentAmountRate.toNumber() + MIN_COLLATERAL_RATE;

    if (companyPreferences.preferredRoundingRule !== null) {
      this.preferredRoundingRule = companyPreferences.preferredRoundingRule;
      this.hasPreferredRoundingRule = true;
    } else {
      this.preferredRoundingRule = RoundingRule.NoRounding;
      this.hasPreferredRoundingRule = false;
    }

    this.defaultOrderSoftLimit = companyPreferences.defaultOrderSoftLimit;
    this.defaultOrderHardLimit = companyPreferences.defaultOrderHardLimit;
    this.aggregateExposureLimit = companyPreferences.aggregateExposureLimit;
    this.executionsValueLimit = companyPreferences.executionsValueLimit;
    this.defaultOmsAgreements = companyPreferences.defaultOmsAgreements || [];
    this.smartloanAgreements = companyPreferences.smartloanAgreements || [];

    this.benchmarkPrice = null;

    if (companyPreferences.benchmarkType) {
      this.benchmarkType = companyPreferences.benchmarkType;
    } else if (companyPreferences.benchmarkPrice) {
      this.benchmarkPrice = companyPreferences.benchmarkPrice;
      this.benchmarkType = 'Custom';
    } else {
      this.benchmarkType = 'OBFR';
    }
    this.cutOffTime = companyPreferences.cutOffTime;
    this.autoDisableInactiveEnabled = companyPreferences.autoDisableInactiveDays !== null;
    this.autoDisableInactiveDays = companyPreferences.autoDisableInactiveDays;

    this.smartLoanIntegrationUrl = companyPreferences.smartLoanIntegrationUrl;
    this.smartLoanIntegrationUsername = companyPreferences.smartLoanIntegrationUsername;
  }

  public async submitForm(): Promise<void> {
    // run validation
    if (this.isProcessing || !this.validateForm()) {
      return;
    }
    this.isProcessing = true;

    const ia = this.preferredCollateralRate
      ? this.preferredCollateralRate - MIN_COLLATERAL_RATE
      : 0;
    try {
      await this.updateCompanyPreferences(
        new Decimal(ia),
        this.preferredRoundingRule,
        this.defaultOrderSoftLimit,
        this.defaultOrderHardLimit,
        this.aggregateExposureLimit,
        this.executionsValueLimit,
        this.benchmarkType,
        this.benchmarkPrice,
        this.defaultOmsAgreements,
        this.smartloanAgreements,
        this.cutOffTime,
        this.smartLoanIntegrationUrl,
        this.smartLoanIntegrationUsername,
        this.autoDisableInactiveEnabled ? this.autoDisableInactiveDays : null
      );

      this.showSuccess = true;
      void wait(1500).then(() => (this.showSuccess = false));

      // reload details
      await this.fetchTraderAdminCompanyPreferences();
      this.formInitialValues = this.getFormModelValues();
      this.$v.$reset();
    } catch (err) {
      this.apiErrors = [`${err}`];
    } finally {
      this.isProcessing = false;
    }
  }

  public validateForm(): boolean {
    this.$v.$reset();
    this.apiErrors = [];
    this.$v.$touch();
    return !this.$v.$anyError;
  }

  public async updateCompanyPreferences(
    rate: Decimal | null,
    roundingRule: RoundingRule | null,
    defaultOrderSoftLimit: Decimal | null,
    defaultOrderHardLimit: Decimal | null,
    aggregateExposureLimit: Decimal | null,
    executionsValueLimit: Decimal | null,
    benchmarkType: string | null,
    benchmarkPrice: Decimal | null,
    defaultOmsAgreements: AgreementInfo[],
    smartloanAgreements: AgreementInfo[],
    cutOffTime: string | null,
    smartLoanIntegrationUrl: string | null,
    smartLoanIntegrationUsername: string | null,
    autoDisableInactiveDays: number | null
  ): Promise<void> {
    try {
      const params = {
        preferredIndependentAmountRate: rate?.toFixed(IA_PRECISION),
        // leave roundingRule NULL if it wasn't set originally and we didn't change it
        preferredRoundingRule:
          this.hasPreferredRoundingRule || roundingRule != RoundingRule.NoRounding
            ? roundingRule
            : null,
        defaultOrderSoftLimit,
        defaultOrderHardLimit,
        aggregateExposureLimit,
        executionsValueLimit,
        defaultOmsAgreementIds: defaultOmsAgreements.map((c) => c.id),
        smartloanAgreementIds: smartloanAgreements.map((c) => c.id),
        cutOffTime,
        autoDisableInactiveDays,
        // because we don't have a separate endpoint just for smartloan integration,
        // we need also need to send smartloan fields
        smartLoanIntegrationUrl,
        smartLoanIntegrationUsername,
        // backend only unsets smartloan integration when url, username and password are all null,
        // so setting password to null will not unset the integration
        smartLoanIntegrationPassword: null,
      };

      const payload =
        this.benchmarkType === 'Custom'
          ? { benchmarkPrice, ...params }
          : { benchmarkType, ...params };

      await axios.put(`/api/1/trader-admin/company`, payload);
    } catch (err) {
      throw new ApiError(i18nServerMessage(err as Error));
    }
  }

  private setSoftLimitFocus(): void {
    this.$refs.softLimitInput?.setFocus();
  }

  private setHardLimitFocus(): void {
    this.$refs.hardLimitInput?.setFocus();
  }
}
</script>

<style lang="scss" scoped>
small {
  font-size: 0.8rem;
}

.v-card .title {
  border-bottom: 1px solid #4a4a4a;
}

.pre-footer-spacer {
  height: 3rem;
}

.fixed-footer {
  position: fixed;
  width: 100%;
  bottom: 0;
  background-color: #1e1e1e;
}
</style>
